import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface Payment {
  id: string;
  tenantId: string;
  paymentToken: string;
  amount: number;
  currency: string;
  subscriptionPlan: string;
  subscriptionDuration: number;
  paymentMethod?: string;
  paymentReference?: string;
  paymentStatus: string;
  paymentDate?: string;
  notes?: string;
  createdAt: string;
  tenant: {
    schoolName: string;
    schoolCode: string;
    contactEmail: string;
  };
}

interface School {
  id: string;
  schoolName: string;
  schoolCode: string;
  contactEmail: string;
  currency: string;
  currencySymbol: string;
  subscriptionPlan: string;
}

const SuperAdminPayments: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  const [payments, setPayments] = useState<Payment[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  
  const [generateForm, setGenerateForm] = useState({
    tenantId: '',
    subscriptionPlan: 'basic',
    subscriptionDuration: 12,
    amount: ''
  });

  const token = localStorage.getItem('superAdminToken');
  const superAdmin = JSON.parse(localStorage.getItem('superAdmin') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/superadmin/login');
      return;
    }
    fetchData();
  }, [token, navigate, filterStatus]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [paymentsRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/payments/all`, { 
          headers,
          params: { status: filterStatus }
        }),
        axios.get(`${API_URL}/superadmin/schools`, { headers })
      ]);

      setPayments(paymentsRes.data.payments);
      setSchools(schoolsRes.data.schools);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/payments/generate-token`,
        generateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Payment token generated: ${response.data.payment.paymentToken}\n\nShare this token with the school to complete payment.`);
      setShowGenerateModal(false);
      setGenerateForm({ tenantId: '', subscriptionPlan: 'basic', subscriptionDuration: 12, amount: '' });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to generate token');
    }
  };

  const handleVerifyPayment = async (status: 'completed' | 'failed', notes: string) => {
    if (!selectedPayment) return;
    
    try {
      await axios.post(
        `${API_URL}/payments/verify/${selectedPayment.id}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Payment ${status === 'completed' ? 'approved' : 'rejected'} successfully!`);
      setShowVerifyModal(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to verify payment');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    navigate('/superadmin/login');
  };

  const copyPaymentLink = (token: string) => {
    const link = `${window.location.origin}/payment/${token}`;
    navigator.clipboard.writeText(link);
    alert('Payment link copied to clipboard!');
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      refunded: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[status] || colors.pending;
  };

  const getPlanBadge = (plan: string) => {
    const colors: any = {
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      basic: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      standard: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      premium: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
    };
    return colors[plan] || colors.trial;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className="bg-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">💳 Payment Management</h1>
              <p className="text-purple-200 text-sm">Welcome, {superAdmin.firstName} {superAdmin.lastName}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/superadmin/dashboard')}
                className="px-4 py-2 bg-purple-700 hover:bg-purple-800 rounded-md transition-colors"
              >
                ← Dashboard
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md hover:bg-purple-700 transition-colors"
              >
                {isDarkMode ? '☀️' : '🌙'}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-purple-700 hover:bg-purple-800 rounded-md transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions Bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-semibold"
          >
            + Generate Payment Token
          </button>
        </div>

        {/* Payments Table */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          <table className="min-w-full">
            <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>School</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Token</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Plan</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Amount</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Reference</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div>
                      <div className="font-medium">{payment.tenant.schoolName}</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{payment.tenant.schoolCode}</div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold">{payment.paymentToken}</span>
                      <button
                        onClick={() => copyPaymentLink(payment.paymentToken)}
                        className="text-purple-600 hover:text-purple-800"
                        title="Copy payment link"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPlanBadge(payment.subscriptionPlan)}`}>
                      {payment.subscriptionPlan.toUpperCase()}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                    {payment.currency} {payment.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(payment.paymentStatus)}`}>
                      {payment.paymentStatus.toUpperCase()}
                    </span>
                  </td>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                    <div className="text-sm">
                      {payment.paymentReference || '-'}
                    </div>
                    {payment.paymentMethod && (
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        via {payment.paymentMethod}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    {payment.paymentStatus === 'processing' && (
                      <button
                        onClick={() => {
                          setSelectedPayment(payment);
                          setShowVerifyModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 font-semibold"
                      >
                        Verify
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate Token Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Generate Payment Token
            </h3>
            <form onSubmit={handleGenerateToken} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select School</label>
                <select
                  value={generateForm.tenantId}
                  onChange={(e) => setGenerateForm({ ...generateForm, tenantId: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  required
                >
                  <option value="">Choose a school...</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.schoolName} ({school.schoolCode})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Subscription Plan</label>
                <select
                  value={generateForm.subscriptionPlan}
                  onChange={(e) => setGenerateForm({ ...generateForm, subscriptionPlan: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="basic">Basic - $50/month</option>
                  <option value="standard">Standard - $100/month</option>
                  <option value="premium">Premium - $200/month</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Duration (months)</label>
                <input
                  type="number"
                  value={generateForm.subscriptionDuration}
                  onChange={(e) => setGenerateForm({ ...generateForm, subscriptionDuration: parseInt(e.target.value) })}
                  className={`w-full px-3 py-2 border rounded-md ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  min="1"
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount (optional, auto-calculated)</label>
                <input
                  type="number"
                  value={generateForm.amount}
                  onChange={(e) => setGenerateForm({ ...generateForm, amount: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Leave empty for auto-calculation"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  className={`flex-1 px-4 py-2 border rounded-md ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Generate Token
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Verify Payment Modal */}
      {showVerifyModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Verify Payment
            </h3>
            
            <div className="space-y-3 mb-6">
              <div>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>School:</span>
                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedPayment.tenant.schoolName}</p>
              </div>
              <div>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedPayment.currency} {selectedPayment.amount}</p>
              </div>
              <div>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payment Method:</span>
                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedPayment.paymentMethod || 'N/A'}</p>
              </div>
              <div>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reference:</span>
                <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedPayment.paymentReference || 'N/A'}</p>
              </div>
              {selectedPayment.notes && (
                <div>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Notes:</span>
                  <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{selectedPayment.notes}</p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowVerifyModal(false);
                  setSelectedPayment(null);
                }}
                className={`flex-1 px-4 py-2 border rounded-md ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleVerifyPayment('failed', 'Payment verification failed')}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Reject
              </button>
              <button
                onClick={() => handleVerifyPayment('completed', 'Payment verified and approved')}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPayments;
